import React, { useState, useRef, useEffect } from 'react';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  MeetingSessionConfiguration
} from 'amazon-chime-sdk-js';

import { fetchAuthSession } from 'aws-amplify/auth';
import { useUser } from '../utils/hooks/useUser';
import { useNavigate } from 'react-router-dom';

const MockInterview = () => {
  const API_URL = process.env.REACT_APP_API_GATEWAY_URL;
  const WS_URL = process.env.REACT_APP_WS_API_URL;
  const { userEmail: userEmailFromHook, userId } = useUser();
  const navigate = useNavigate();
  const [isInterviewStarted, setIsInterviewStarted] = useState(false);
  const [isAudioStarted, setIsAudioStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [interviewType, setInterviewType] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [customPosition, setCustomPosition] = useState('');
  const [showCustomPositionInput, setShowCustomPositionInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [interviewStatus, setInterviewStatus] = useState('idle');
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [wsConnection, setWsConnection] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null); // Store session_id in ref for consistency
  const interviewStartTimeRef = useRef(null); // Store interview start timestamp
  const [voiceStatus, setVoiceStatus] = useState('Initializing voice...');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [interviewState, setInterviewState] = useState('not_started');
  const [introductionDone, setIntroductionDone] = useState(false);
  const [lastUserActivity, setLastUserActivity] = useState(null);
  const [silenceTimer, setSilenceTimer] = useState(null);
  const [isSilenceBufferActive, setIsSilenceBufferActive] = useState(false);
  const [currentUserMessage, setCurrentUserMessage] = useState('');
  const [pendingAIResponse, setPendingAIResponse] = useState(false);
  const messagesEndRef = useRef(null);
  const accumulatedTranscriptRef = useRef(''); // Store accumulated transcription
  const [isProcessing, setIsProcessing] = useState(false); // Prevent concurrent Bedrock calls
  const [pendingTranscripts, setPendingTranscripts] = useState([]); // Accumulate transcripts while processing
  const [countdown, setCountdown] = useState(null); // Countdown timer state
  const [pinnedMessage, setPinnedMessage] = useState(null); // Track pinned message
  const [showNotes, setShowNotes] = useState(true); // Toggle notes panel - expanded by default
  const [notes, setNotes] = useState(''); // User notes/code
  const notesRef = useRef(''); // Ref to track current notes value
  const [silenceStartTime, setSilenceStartTime] = useState(null); // Track when silence started
  const [accumulatedMessage, setAccumulatedMessage] = useState(''); // Buffer for accumulating messages
  const [activeTab, setActiveTab] = useState('new');
  const [interviewHistory, setInterviewHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(null); // 30-minute session timer
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(30 * 60); // 2 minutes in seconds for testing
  const [sessionTimerInterval, setSessionTimerInterval] = useState(null); // Timer interval reference
  const [isEndingInterview, setIsEndingInterview] = useState(false); // Track if interview is ending
  const [showTimeWarning, setShowTimeWarning] = useState(false);

  // Update notes ref when notes state changes
  useEffect(() => {
    notesRef.current = notes; // Update ref when notes state changes
  }, [notes]);

  // Use refs for SDK instances
  const deviceController = useRef(null);
  const meetingSession = useRef(null);
  const logger = useRef(new ConsoleLogger('MeetingLogs'));
  const meetingEnded = useRef(false);
  const samanthaVoiceRef = useRef(null);
  const wsConnectionRef = useRef(null);

  // Fetch interview history
  const fetchInterviewHistory = async () => {
    try {
      setHistoryLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${API_URL}/history/interview`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,GET'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setInterviewHistory(data.history || []);
    } catch (error) {
      setError('Failed to load interview history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch interview details
  const fetchInterviewDetails = async (sessionId) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${API_URL}/history/interview?id=${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,GET'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSelectedInterview(data);
      setShowInterviewDetails(true);
    } catch (error) {
      setError('Failed to load interview details');
    }
  };

  // Function to handle smart batching with silence buffer
  const sendMessageWithSilenceBuffer = (message, currentNotes = '') => {
    // Clear any existing timer
    if (silenceTimer) {
      clearTimeout(silenceTimer);
    }

    // Cancel any pending AI response when user continues speaking
    if (pendingAIResponse) {
      setPendingAIResponse(false);
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
    }

    // Accumulate the message
    const newAccumulatedMessage = accumulatedMessage ? `${accumulatedMessage} ${message}` : message;
    setAccumulatedMessage(newAccumulatedMessage);

    // Calculate silence duration
    const silenceDuration = silenceStartTime ? Date.now() - silenceStartTime : 0;

    // Check if we should make a Bedrock call
    const shouldCall = shouldMakeBedrockCall(newAccumulatedMessage, silenceDuration);

    if (shouldCall) {
      // Send immediately if conditions are met
      sendAccumulatedMessage(newAccumulatedMessage, currentNotes);
      setAccumulatedMessage(''); // Clear accumulated message
    } else {
      // Set a 1.2 second silence buffer to wait for more input
      const timer = setTimeout(() => {
        // Check again after silence - maybe user is done
        const finalSilenceDuration = silenceStartTime ? Date.now() - silenceStartTime : 0;
        const finalShouldCall = shouldMakeBedrockCall(newAccumulatedMessage, finalSilenceDuration);
        
        if (finalShouldCall) {
          sendAccumulatedMessage(newAccumulatedMessage, currentNotes);
          setAccumulatedMessage('');
        }
      }, 1200); // 1.2 seconds of silence

      setSilenceTimer(timer);
      setIsSilenceBufferActive(true);
    }
  };

  // Helper function to send accumulated message to backend
  const sendAccumulatedMessage = async (message, currentNotes) => {
    if (wsConnectionRef.current && wsConnectionRef.current.readyState === WebSocket.OPEN) {
      // Check if we're already processing a call
      if (isProcessing) {
        setPendingTranscripts(prev => [...prev, message]);
        return;
      }

      setIsWaitingForResponse(true);
      setPendingAIResponse(true);
      setIsSilenceBufferActive(false);
      setIsProcessing(true); // Mark as processing
      setInterviewStatus('listening'); // Set to listening when waiting for AI response
      
      const messageData = {
        action: 'processTranscript',
        message: message,
        notes: currentNotes,
        session_id: sessionIdRef.current,
        user_id: userId, // Use actual user ID from Cognito
        type: 'user',
        interview_position: customPosition || selectedPosition,
        company: selectedCompany,
        exp_level: selectedLevel,
        interview_type: interviewType
      };
      wsConnectionRef.current.send(JSON.stringify(messageData));
    }
  };

  // Function to reset silence timer when user speaks (speech resumption detection)
  const resetSilenceTimer = () => {
    if (silenceTimer) {
      clearTimeout(silenceTimer);
      setSilenceTimer(null);
    }
    setIsSilenceBufferActive(false);
    setLastUserActivity(Date.now());
    setSilenceStartTime(null); // Reset silence tracking
    
    // Cancel any pending AI response when user continues speaking
    if (pendingAIResponse) {
      setPendingAIResponse(false);
      window.speechSynthesis.cancel();
    }

    // Clear pending transcripts when user resumes speaking
    if (pendingTranscripts.length > 0) {
      setPendingTranscripts([]);
    }
  };

  // Function to evaluate if we should make a Bedrock call based on message content and context
  const shouldMakeBedrockCall = (message, silenceDuration = 0) => {
    const trimmedMessage = message.trim();
    
    // Skip calls for very short messages or thinking sounds
    if (trimmedMessage.length < 10) {
      return false;
    }
    
    // Check if the entire message is just thinking sounds
    const thinkingWords = ['um', 'uh', 'hmm', 'er', 'ah'];
    const lowerMessage = trimmedMessage.toLowerCase();
    const isOnlyThinkingSounds = thinkingWords.some(word => 
      lowerMessage === word || lowerMessage === word + ' ' || lowerMessage === ' ' + word
    );
    
    if (isOnlyThinkingSounds) {
      return false;
    }
    
    // Scenario 1: User asks a question (contains "?", "what", "how", "why", etc.)
    const questionIndicators = ['?', 'what', 'how', 'why', 'when', 'where', 'which', 'who', 'can you', 'could you', 'would you'];
    const hasQuestion = questionIndicators.some(indicator => 
      lowerMessage.includes(indicator.toLowerCase())
    );
    if (hasQuestion) {
      return true;
    }
    
    // Scenario 2: User gives a substantial response (> 100 words)
    const wordCount = trimmedMessage.split(/\s+/).length;
    if (wordCount > 100) {
      return true;
    }
    
    // Scenario 3: User explicitly requests feedback
    const feedbackRequests = ['what do you think', 'is this correct', 'feedback please', 'am i right', 'how did i do'];
    const requestsFeedback = feedbackRequests.some(request => 
      lowerMessage.includes(request.toLowerCase())
    );
    if (requestsFeedback) {
      return true;
    }
    
    // Scenario 4: User indicates completion
    const completionIndicators = ["that's it", "i'm done", "finished", "that's all", "complete"];
    const indicatesCompletion = completionIndicators.some(indicator => 
      lowerMessage.includes(indicator.toLowerCase())
    );
    if (indicatesCompletion) {
      return true;
    }
    
    // Scenario 5: System message (handled separately in backend)
    
    // Scenario 6: After 5+ seconds of silence (only if message is substantial)
    if (silenceDuration > 5000 && wordCount > 20) {
      return true;
    }
    
    // Scenario 7: User asks for next question
    const nextQuestionRequests = ['next question', 'move on', 'continue', 'next', 'proceed'];
    const requestsNextQuestion = nextQuestionRequests.some(request => 
      lowerMessage.includes(request.toLowerCase())
    );
    if (requestsNextQuestion) {
      return true;
    }
    
    // Scenario 8: User provides code/technical explanation (contains code indicators or > 50 words)
    const codeIndicators = ['function', 'class', 'method', 'algorithm', 'complexity', 'o(', 'time complexity', 'space complexity'];
    const hasCodeContent = codeIndicators.some(indicator => 
      lowerMessage.includes(indicator.toLowerCase())
    );
    if (hasCodeContent || wordCount > 50) {
      return true;
    }
    
    // Default: Don't make a call for incomplete thoughts
    return false;
  };

  // Function to wait for voices to load
  const waitForVoices = () => {
    return new Promise((resolve) => {
      let voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        resolve(voices);
        return;
      }

      // If voices aren't loaded yet, wait for the event
      const onVoicesChanged = () => {
        voices = window.speechSynthesis.getVoices();
        window.speechSynthesis.onvoiceschanged = null; // Remove the listener
        resolve(voices);
      };
      window.speechSynthesis.onvoiceschanged = onVoicesChanged;
    });
  };

  // Device initialization effect
  useEffect(() => {
    initializeDevices();
    
    return () => {
      if (meetingSession.current) {
        meetingSession.current.audioVideo.stop();
      }
      if (deviceController.current) {
        deviceController.current.destroy();
      }
      if (wsConnection) {
        wsConnection.close();
        setWsConnection(null);
      }
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
    };
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);



  useEffect(() => {
    if (activeTab === 'review' && userEmailFromHook) {
      fetchInterviewHistory();
    }
  }, [activeTab, userEmailFromHook]);

  // Audio observer setup
  const audioVideoObserver = {
    audioVideoDidStart: () => {
      setIsAudioStarted(true);
    },
    audioVideoDidStop: async (sessionStatus) => {
      // Status code 1: Connection failed, can retry
      // Status code 5: Meeting ended, don't retry
      if (sessionStatus?._statusCode === 1 && !meetingEnded.current) {
        try {
          if (meetingSession.current && deviceController.current) {
            const audioDevices = await deviceController.current.listAudioInputDevices();
            const selectedDevice = audioDevices.find(device => device.deviceId === selectedAudioDevice) || audioDevices[0];
            await meetingSession.current.audioVideo.startAudioInput(selectedDevice);
            await meetingSession.current.audioVideo.start();
          }
        } catch (error) {
          setError('Audio connection lost. Please try rejoining the meeting.');
        }
      } else if (sessionStatus?._statusCode === 5) {
        setError('Meeting has ended. Please start a new meeting if needed.');
        setIsInterviewStarted(false);
      }
      setIsAudioStarted(false);
    },
    audioVideoDidStartConnecting: (reconnecting) => {},
    audioInputDidBecomeGood: () => {
      setError('');
    },
    audioInputDidBecomeBad: () => {
      setError('Poor audio input quality detected');
    },
    audioInputDidBecomeMuted: () => {},
    audioInputDidBecomeUnmuted: () => {},
    connectionDidBecomePoor: () => {},
    // Required empty implementations for AudioVideoObserver interface
    videoTileDidUpdate: () => {},
    videoTileWasRemoved: () => {},
    videoAvailabilityDidChange: () => {},
    metricsDidReceive: () => {},
    connectionDidSuggestStopVideo: () => {},
    videoSendDidBecomeUnavailable: () => {},
    encodingSimulcastLayersDidChange: () => {},
    remoteVideoSourcesDidChange: () => {},
    cameraSendDidBecomeUnavailable: () => {}
  };

  // Add this function
  const initializeDevices = async () => {
    try {
      deviceController.current = new DefaultDeviceController(logger.current);
      const devices = await deviceController.current.listAudioInputDevices();
      setAudioInputDevices(devices);
      if (devices.length > 0) {
        setSelectedAudioDevice(devices[0].deviceId);
      }
    } catch (err) {
      setError('Failed to initialize audio devices');
    }
  };

  const createChimeMeeting = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      const response = await fetch(`${API_URL}/mock-interview`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify({
          action: 'create'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create meeting');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  const joinChimeMeeting = async (meetingInfo) => {
    try {
      // Configure meeting with received info
      
      const configuration = new MeetingSessionConfiguration(
        meetingInfo.Meeting,
        meetingInfo.Attendee
      );

      // Enable basic features
      configuration.enableUnifiedPlanForChromiumBasedBrowsers = true;
      configuration.enableSimulcastForUnifiedPlanChromiumBasedBrowsers = true;
      configuration.enableWebAudio = true;

      // Create new meeting session
      meetingSession.current = new DefaultMeetingSession(
        configuration,
        logger.current,
        deviceController.current
      );

      // Add audio observer and setup transcription
      meetingSession.current.audioVideo.addObserver(audioVideoObserver);

      // Subscribe to transcription events through the transcription controller once
      if (!meetingSession.current.transcriptionEventHandler) {
        meetingSession.current.transcriptionEventHandler = (transcriptEvent) => {
          try {
            if (transcriptEvent.results?.[0]?.alternatives?.[0]?.transcript) {
              const transcript = transcriptEvent.results[0].alternatives[0].transcript;
              const isPartial = transcriptEvent.results[0].isPartial;
              const canSend = !isWaitingForResponse && wsConnectionRef.current !== null;
              
              // Filter out very short transcripts and likely background noise
              if (transcript.trim().length <= 3 || /^[.,!?]/.test(transcript)) {
                return;
              }

              if (isPartial) {
                // For partial results, show the complete transcript so far
                setCurrentTranscript(transcript);
                
                // Update UI with the complete transcript so far
                // COMMENTED OUT: setMessages(prev => {
                //   const newMessages = [...prev];
                //   if (newMessages.length > 0 && newMessages[newMessages.length - 1].type === 'user') {
                //     const oldContent = newMessages[newMessages.length - 1].content;
                //     console.log('Replacing user message content:');
                //     console.log('  Old content:', oldContent);
                //     console.log('  New content:', transcript);
                //     newMessages[newMessages.length - 1].content = transcript;
                //   } else {
                //     console.log('Creating new user message with content:', transcript);
                //     newMessages.push({
                //       type: 'user',
                //       content: transcript
                //     });
                //   }
                //   return newMessages;
                // });
              } else {
                // For final results, use the complete transcript
                const finalTranscript = transcript;
                setCurrentTranscript(finalTranscript);
                
                // Add final transcript to chat (Chime has already determined it's complete)
                setMessages(prev => {
                  const newMessages = [...prev];
                  // Only add if it's a new message (not replacing existing)
                  newMessages.push({
                    type: 'user',
                    content: finalTranscript
                  });
                  return newMessages;
                });

                // Set silence start time when user stops speaking
                setSilenceStartTime(Date.now());
                
                // Send final transcript to backend with buffering to prevent interruption
                if (canSend) {
                  // Use silence buffer to allow user to continue speaking if needed
                  sendMessageWithSilenceBuffer(finalTranscript, notesRef.current || '');
                }
                
                // Clear accumulated transcript for next utterance
                accumulatedTranscriptRef.current = '';
              }
            }
          } catch (error) {
            setError('Error processing transcript');
          }
        };
        meetingSession.current.audioVideo.transcriptionController?.subscribeToTranscriptEvent(
          meetingSession.current.transcriptionEventHandler
        );
      }

      // Subscribe to general realtime messages as well
      meetingSession.current.audioVideo.realtimeSubscribeToReceiveDataMessage('*', (dataMessage) => {});

      // Connect audio
      try {
        // Get available audio devices and select one
        const audioDevices = await deviceController.current.listAudioInputDevices();
        const selectedDevice = audioDevices.find(device => device.deviceId === selectedAudioDevice);
        
        // Start audio with the device
        await meetingSession.current.audioVideo.startAudioInput(selectedDevice || audioDevices[0]);
        await meetingSession.current.audioVideo.start();

        // Bind audio element
        const audioElement = document.getElementById('audio-element');
        await meetingSession.current.audioVideo.bindAudioElement(audioElement);

        // Remove the hardcoded greeting since Claude will introduce itself
        
      } catch (error) {
        throw error;
      }
      
      setIsAudioStarted(true);
    } catch (error) {
      throw error;
    }
  };

  // Update the connectWebSocket function
  const connectWebSocket = async (session_id) => {
    try {
      // Get the current session token
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();
      
      // Add session_id back to the URL
      const wsUrl = `${WS_URL}?session_id=${session_id}&token=${idToken}`;
      
      // Add authorization header to WebSocket connection
      const ws = new WebSocket(wsUrl, [], {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      ws.onopen = () => {
        wsConnectionRef.current = ws;
        setWsConnection(ws);
        // Send initial message with authorization and interview details
        const positionToUse = customPosition || selectedPosition;
        const initialMessage = `Start the mock interview for a ${selectedLevel} level ${positionToUse} position at ${selectedCompany}. This is a ${interviewType} interview. Introduce yourself briefly and begin with your first question.`;
        
        const messageData = {
          action: 'processTranscript',
          message: initialMessage,
          token: idToken,
          session_id: session_id,  // Use the passed session_id parameter
          user_id: userId,  // Use actual user ID from Cognito
          type: 'system',         // Indicate this is system message
          interview_position: customPosition || selectedPosition,
          company: selectedCompany,
          exp_level: selectedLevel,
          interview_type: interviewType
        };
        wsConnectionRef.current.send(JSON.stringify(messageData));
      };
      
      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'chunk') {
          // Skip displaying chunks
        } else if (data.type === 'complete') {
          // Reset waiting state before processing message
          setIsWaitingForResponse(false);
          setPendingAIResponse(false);
          setIsProcessing(false); // Reset processing flag
          setInterviewStatus('talking'); // Set to talking when AI is speaking
          await new Promise(resolve => setTimeout(resolve, 100)); // Small delay to ensure state updates

          // Update interview state if provided
          if (data.interview_state) {
            setInterviewState(data.interview_state);
          }
          if (data.introduction_done !== undefined) {
            setIntroductionDone(data.introduction_done);
          }

          // Only update display and speak for complete messages
          setMessages(prev => {
            const newMessages = [...prev];
            if (newMessages.length > 0 && newMessages[newMessages.length - 1].type === 'interviewer') {
              newMessages[newMessages.length - 1].content = data.content;
            } else {
              const newMessage = {
                type: 'interviewer',
                content: data.content
              };
              newMessages.push(newMessage);
            }
            return newMessages;
          });

          // Speak the response
          try {
            const utterance = new SpeechSynthesisUtterance(data.content);
            utterance.voice = samanthaVoiceRef.current;
            utterance.onend = () => {
              setInterviewStatus('listening'); // Set back to listening when AI finishes speaking
            };
            window.speechSynthesis.speak(utterance);
          } catch (error) {
            setError('Speech error');
            setInterviewStatus('listening'); // Set to listening on error
          }

          // Process any pending transcripts after response completes
          if (pendingTranscripts.length > 0) {
            const combinedMessage = pendingTranscripts.join(' ');
            setPendingTranscripts([]); // Clear pending transcripts
            // Send the combined message
            sendMessageWithSilenceBuffer(combinedMessage, notesRef.current || '');
          }
        }
      };

      ws.onerror = (error) => {
        wsConnectionRef.current = null;
        setWsConnection(null);
        setIsWaitingForResponse(false); // Reset waiting state on error
        setIsProcessing(false); // Reset processing flag on error
        setError('WebSocket connection error');
      };

      ws.onclose = () => {
        wsConnectionRef.current = null;
        setWsConnection(null);
        setIsWaitingForResponse(false); // Reset waiting state on close
        setIsProcessing(false); // Reset processing flag on close
      };

      return ws;
    } catch (error) {
      wsConnectionRef.current = null;  // Clear ref on error
      setWsConnection(null);
      setError('Failed to connect to WebSocket');
      throw error;
    }
  };

  const startInterview = async () => {
    if (!API_URL) {
      setError('API URL is not configured');
      return;
    }

    if (!interviewType) {
      setError('Please select an interview type');
      return;
    }

    if (!selectedPosition && !customPosition) {
      setError('Please select a position or enter a custom position');
      return;
    }

    if (!selectedCompany) {
      setError('Please select a company');
      return;
    }

    if (!selectedLevel) {
      setError('Please select an experience level');
      return;
    }

    if (!deviceController.current) {
      setError('Audio devices not initialized. Please wait a moment and try again.');
      return;
    }

    setIsLoading(true);
    try {
      // Check quota availability for interview processing
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();

      // Check if userId is available
      if (!userId) {
        throw new Error('User ID not available. Please refresh the page and try again.');
      }

      const quotaResponse = await fetch(`${API_URL}/check-subscription`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify({ 
          user_id: userId,
          email: userEmailFromHook,
          action: 'validate_quota',
          quota_type: 'interview'
        })
      });

      const quotaData = await quotaResponse.json();
      
      if (!quotaData.hasQuota) {
        // If no quota available, show error message
        setError('No quota remaining for mock interviews. Please upgrade your subscription to continue.');
        return;
      }

      if (quotaData.remainingQuota <= 0) {
        setError('No interview sessions remaining. Please upgrade your subscription to continue.');
        return;
      }

      // Initialize voice first - now properly waiting for voices
      const voices = await waitForVoices();
      const samantha = voices.find(voice => 
        voice.name.toLowerCase().includes('samantha') || 
        (voice.lang === 'en-US' && voice.name.toLowerCase().includes('female'))
      );
      
      if (samantha) {
        samanthaVoiceRef.current = samantha;
        setSelectedVoice(samantha.name);
      } else {
        console.warn('No suitable voice found, will use default voice');
      }

      // Create and join meeting
      const meetingInfo = await createChimeMeeting();
      await joinChimeMeeting(meetingInfo);

      // Set session ID first, then connect WebSocket
      const currentSessionId = meetingInfo.Meeting.MeetingId;
      setSessionId(currentSessionId);
      sessionIdRef.current = currentSessionId; // Store in ref for consistency
      interviewStartTimeRef.current = Date.now(); // Store interview start timestamp
      // Connect WebSocket
      const ws = await connectWebSocket(currentSessionId);
      setIsInterviewStarted(true);
      setIsLoading(false);
      
      // Start countdown timer
      setCountdown(5);
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      
      setInterviewStatus('talking');
      
      // Start 30-minute session timer
      const sessionDuration = 30 * 60; // 30 minutes in seconds
      setSessionTimeRemaining(sessionDuration);
      
      const interval = setInterval(() => {
        setSessionTimeRemaining(prev => {
          const newTime = prev - 1;
          
          if (newTime === 5 * 60) { // Show warning at 25 minutes (5 minutes remaining)
            setShowTimeWarning(true);
            // Remove the alert
            setTimeout(() => {
              setShowTimeWarning(false);
            }, 10000); // Hide after 10 seconds
          }
          
          if (newTime <= 0) {
            clearInterval(interval);
            // Call endInterview to properly close connection and deduct quota
            endInterview();
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
      
      setSessionTimerInterval(interval);
    } catch (error) {
      setError(`Failed to start interview: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const endInterview = async () => {
    // Show ending interview message and set flag
    setError('Ending interview... Please wait.');
    setIsEndingInterview(true);
    
    // Cancel any ongoing speech synthesis immediately
    window.speechSynthesis.cancel();
    
    try {
      meetingEnded.current = true;

      // Stop Chime meeting first
      if (meetingSession.current) {
        // Unsubscribe from transcription events first
        if (meetingSession.current.transcriptionEventHandler) {
          meetingSession.current.audioVideo.transcriptionController?.unsubscribeFromTranscriptEvent(
            meetingSession.current.transcriptionEventHandler
          );
          meetingSession.current.transcriptionEventHandler = null;
        }
        
        // Remove observer before stopping
        meetingSession.current.audioVideo.removeObserver(audioVideoObserver);
      
        // Stop audio
        await meetingSession.current.audioVideo.stopAudioInput();
        await meetingSession.current.audioVideo.stop();
        
        const session = await fetchAuthSession();
        const idToken = session.tokens.idToken.toString();
        
        // Then end the meeting in backend
        await fetch(`${API_URL}/mock-interview`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
            'access-control-allow-headers': 'content-type,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods,Authorization',
            'access-control-allow-origin': '*',
            'access-control-allow-methods': 'OPTIONS,POST'
          },
          body: JSON.stringify({
            action: 'end',
            meetingId: meetingSession.current.configuration.meetingId
          })
        });
      }

      // Send final update to backend with interview completion data
      if (wsConnectionRef.current && wsConnectionRef.current.readyState === WebSocket.OPEN) {
        // Debug duration calculation
        const currentTime = Date.now();
        const startTime = interviewStartTimeRef.current;
        const duration = startTime ? Math.floor((currentTime - startTime) / 1000) : 0;
        
        // Send interview completion data
        const endMessageData = {
          action: 'endInterview',
          session_id: sessionIdRef.current,
          user_id: userId,
          interview_state: 'completed',
          duration: duration,
          notes: notes,
          interview_position: customPosition || selectedPosition,
          company: selectedCompany,
          exp_level: selectedLevel,
          interview_type: interviewType
        };
        
        wsConnectionRef.current.send(JSON.stringify(endMessageData));
        
        // Wait for messages to be sent
        await new Promise((resolve) => setTimeout(resolve, 500));
        
        // Now close the WebSocket
        wsConnectionRef.current.close();
        wsConnectionRef.current = null;
        setWsConnection(null);
      }

      // Clear the device controller
      if (deviceController.current) {
        await deviceController.current.destroy();
        deviceController.current = null;
      }
    } catch (error) {
      setError(`Error ending interview: ${error.message}`);
    } finally {
      // Reinitialize devices after cleanup
      await initializeDevices();
      
      meetingEnded.current = false;
      setIsInterviewStarted(false);
      setIsAudioStarted(false);
      meetingSession.current = null;
      setMessages([]);
      setInterviewType('');
      setSelectedPosition('');
      setSelectedCompany('');
      setSelectedLevel('');
      setCustomPosition('');
      setShowCustomPositionInput(false);
      sessionIdRef.current = null;
      interviewStartTimeRef.current = null;
      setInterviewStatus('idle');
      setCurrentTranscript('');
      setError('');
      setInterviewState('not_started');
      setIntroductionDone(false);
      if (silenceTimer) {
        clearTimeout(silenceTimer);
        setSilenceTimer(null);
      }
      setLastUserActivity(null);
      setIsSilenceBufferActive(false);
      setPendingAIResponse(false);
      setCurrentUserMessage('');
      setIsProcessing(false);
      setPendingTranscripts([]);
      accumulatedTranscriptRef.current = '';
      setCountdown(null);
      setPinnedMessage(null);
      setShowNotes(false);
      setNotes('');
      
      // Clear session timer
      if (sessionTimerInterval) {
        clearInterval(sessionTimerInterval);
        setSessionTimerInterval(null);
      }
      setSessionTimeRemaining(30 * 60);
      setIsEndingInterview(false);
    }
  };

  const renderRecordingControls = () => (
    <div className="mt-auto flex flex-col items-center space-y-2">
      {/* Removed redundant controls - End Interview button is in header */}
    </div>
  );

  return (
    <div className="h-screen w-full">
      {/* Centered tabs at the top */}
      <div className={`${!isInterviewStarted ? 'max-w-4xl mx-auto px-6' : ''}`}>
        {!isInterviewStarted && (
          <>
            <h1 className="text-2xl font-bold mb-6 mt-6">Mock Interview</h1>
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('new')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'new'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  New Mock Interview
                </button>
                <button
                  onClick={() => setActiveTab('review')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'review'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Review Past Interviews
                </button>
              </nav>
            </div>
          </>
        )}

        {/* Tab Content */}
        {activeTab === 'new' && (
          <div className={`${!isInterviewStarted ? '' : 'h-[calc(100vh-48px)]'}`}>
            {showTimeWarning && (
              <div className="fixed top-0 left-0 right-0 bg-yellow-100 border-b border-yellow-200 p-4 text-center animate-fade-in z-50">
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-yellow-800 font-medium">
                    ⚠️ 1 minute remaining in your interview session. Please wrap up your responses.
                  </span>
                </div>
              </div>
            )}

            <audio id="audio-element" style={{ display: 'none' }} />
            
            {error && (
              <div className={`fixed top-0 left-0 right-0 ${
                isEndingInterview 
                  ? 'bg-blue-100 border-b border-blue-200' 
                  : 'bg-red-100 border-b border-red-400'
              } p-4 text-center animate-fade-in z-50`}>
                <div className="flex items-center justify-center space-x-2">
                  {isEndingInterview && (
                    <svg className="w-6 h-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span className={`font-medium ${isEndingInterview ? 'text-blue-800' : 'text-red-800'}`}>
                    {error}
                  </span>
                </div>
              </div>
            )}

            {!isInterviewStarted ? (
              <div className="max-w-4xl mx-auto mt-8">
                <div className="bg-white rounded-md shadow-sm border border-gray-200 p-8">
                  <select
                    className="w-full p-2 mb-4 bg-white border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={selectedPosition}
                    onChange={(e) => {
                      setSelectedPosition(e.target.value);
                      if (e.target.value === 'other') {
                        setShowCustomPositionInput(true);
                        setSelectedPosition('');
                      } else {
                        setShowCustomPositionInput(false);
                        setCustomPosition('');
                      }
                    }}
                  >
                    <option value="">Select position</option>
                    <option value="Software Engineer">Software Engineer</option>
                    <option value="Data Engineer">Data Engineer</option>
                    <option value="Product Manager">Product Manager</option>
                    <option value="Data Scientist">Data Scientist</option>
                    <option value="DevOps Engineer">DevOps Engineer</option>
                    <option value="Frontend Developer">Frontend Developer</option>
                    <option value="Backend Developer">Backend Developer</option>
                    <option value="Full Stack Developer">Full Stack Developer</option>
                    <option value="other">Other (specify)</option>
                  </select>
                  
                  {showCustomPositionInput && (
                    <input
                      type="text"
                      placeholder="Enter your position"
                      value={customPosition}
                      onChange={(e) => setCustomPosition(e.target.value)}
                      className="w-full p-2 mb-3 bg-white border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  )}
                  
                  <select
                    className="w-full p-2 mb-3 bg-white border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">Select company</option>
                    <option value="Google">Google</option>
                    <option value="Microsoft">Microsoft</option>
                    <option value="Amazon">Amazon</option>
                    <option value="Meta">Meta</option>
                    <option value="Apple">Apple</option>
                    <option value="Netflix">Netflix</option>
                    <option value="Uber">Uber</option>
                    <option value="Airbnb">Airbnb</option>
                    <option value="Stripe">Stripe</option>
                    <option value="Salesforce">Salesforce</option>
                    <option value="Other">Other</option>
                  </select>
                  
                  <select
                    className="w-full p-2 mb-3 bg-white border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={interviewType}
                    onChange={(e) => setInterviewType(e.target.value)}
                  >
                    <option value="">Select interview type</option>
                    <option value="technical">Technical Interview</option>
                    <option value="behavioral">Behavioral Interview</option>
                    <option value="system design">System Design Interview</option>
                    <option value="coding">Coding Interview</option>
                  </select>
                  
                  <select
                    className="w-full p-2 mb-3 bg-white border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                  >
                    <option value="">Select experience level</option>
                    <option value="beginner">Beginner (0-2 years)</option>
                    <option value="intermediate">Intermediate (3-5 years)</option>
                    <option value="senior">Senior (6+ years)</option>
                    <option value="lead">Lead/Manager (8+ years)</option>
                    <option value="director">Director/Principal (10+ years)</option>
                  </select>
                  
                  <select
                    className="w-full p-2 mb-3 bg-white border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={selectedAudioDevice}
                    onChange={(e) => setSelectedAudioDevice(e.target.value)}
                  >
                    {audioInputDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId}`}
                      </option>
                    ))}
                  </select>

                  <button
                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    style={{ backgroundColor: '#2563eb' }}
                    onClick={startInterview}
                    disabled={isLoading || (!selectedPosition && !customPosition) || !selectedCompany || !interviewType || !selectedLevel}
                  >
                    {isLoading ? 'Setting up...' : 'Start Interview'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex h-full">
                {/* Left panel - Transcripts */}
                <div className="w-1/2 h-full border-r border-gray-200 flex flex-col">
                  {/* Interview header with controls */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                          {interviewType.charAt(0).toUpperCase() + interviewType.slice(1)} Interview
                        </span>
                        <div className={`px-3 py-1 rounded text-sm font-medium ${
                          sessionTimeRemaining <= 5 * 60 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          Time: {Math.floor(sessionTimeRemaining / 60)}:{String(sessionTimeRemaining % 60).padStart(2, '0')}
                        </div>
                      </div>
                      <button
                        onClick={endInterview}
                        className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600"
                      >
                        End Interview
                      </button>
                    </div>
                  </div>

                  {/* Transcripts */}
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-2">
                      {messages.map((message, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded-lg ${
                            message.type === 'interviewer'
                              ? 'bg-blue-50 text-blue-800'
                              : 'bg-gray-50 text-gray-800'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-sm">
                                {message.type === 'interviewer' ? 'Interviewer' : 'You'}:
                              </span>
                              {message.type === 'interviewer' && (
                                <button
                                  onClick={() => setPinnedMessage(pinnedMessage?.content === message.content ? null : message)}
                                  className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                                >
                                  {pinnedMessage?.content === message.content ? '📌 Unpin' : '📌 Pin'}
                                </button>
                               )}
                            </div>
                          </div>
                          <div className="text-sm">{message.content}</div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </div>

                {/* Right panel - Notes and Interview Info */}
                <div className="w-1/2 h-full flex flex-col">
                  {/* Pinned question at top */}
                  {pinnedMessage && (
                    <div className="border-b border-gray-200 p-4 bg-yellow-50">
                      <div className="font-medium text-gray-700">Current Question:</div>
                      <div className="mt-1">{pinnedMessage.content}</div>
                    </div>
                  )}

                  {/* Notes section */}
                  <div className="flex-grow p-4 overflow-y-auto">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes & Code
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full h-[calc(100vh-300px)] p-3 border rounded-lg"
                        placeholder="Take notes or write code here..."
                      />
                    </div>

                    {/* Interview info */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Interview Details</h3>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div>Position: {customPosition || selectedPosition}</div>
                        <div>Company: {selectedCompany}</div>
                        <div>Level: {selectedLevel}</div>
                        <div>Type: {interviewType}</div>
                        {sessionTimeRemaining && (
                          <div>Time Remaining: {Math.floor(sessionTimeRemaining / 60)}:{String(sessionTimeRemaining % 60).padStart(2, '0')}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'review' && (
          <div className="space-y-6">
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📝</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Past Mock Interviews</h3>
              <p className="text-gray-600 mb-6">View and review your previous mock interviews and feedback.</p>
            </div>
            
            {historyLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading interview history...</p>
              </div>
            ) : interviewHistory.length > 0 ? (
              <div className="space-y-4">
                {interviewHistory.map((interview, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          Interview #{interviewHistory.length - index}
                        </h4>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Date:</span> {new Date(interview.SubmissionDateTime).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Position:</span> {interview.role || 'Not specified'}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Company:</span> {interview.company || 'Not specified'}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">State:</span> {interview.interview_state || 'Not specified'}
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Status:</span> {interview.status || 'Not specified'}
                        </p>
                        {interview.duration && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">Duration:</span> {Math.floor(interview.duration / 60)}m {interview.duration % 60}s
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/interview-results?session_id=${interview.sessionId}`)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
                <p className="text-sm text-gray-500 text-center">No past interviews found. Start a new mock interview to see it here.</p>
              </div>
            )}
          </div>
        )}
        
        {/* {activeTab === 'study' && (
          <div className="space-y-6">
            <div className="text-center py-8">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Study Plan Dashboard</h3>
              <p className="text-gray-600 mb-6">Track your progress and improve your interview skills.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">📊 Progress Overview</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Interviews Completed</span>
                    <span className="font-medium">{interviewHistory.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average Duration</span>
                    <span className="font-medium">
                      {interviewHistory.length > 0 
                        ? `${Math.floor(interviewHistory.reduce((sum, interview) => sum + (interview.duration || 0), 0) / interviewHistory.length / 60)}m`
                        : '0m'
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Study Plan Progress</span>
                    <span className="font-medium text-green-600">25%</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">🎯 Skill Categories</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Technical Skills</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{width: '60%'}}></div>
                      </div>
                      <span className="text-xs font-medium">60%</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Communication</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{width: '75%'}}></div>
                      </div>
                      <span className="text-xs font-medium">75%</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Problem Solving</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-yellow-600 h-2 rounded-full" style={{width: '45%'}}></div>
                      </div>
                      <span className="text-xs font-medium">45%</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">🏆 Recent Achievements</h4>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <span className="text-yellow-500 mr-2">🥇</span>
                    <span>First Technical Interview</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="text-blue-500 mr-2">🥈</span>
                    <span>Completed 5 Questions</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="text-green-500 mr-2">🥉</span>
                    <span>Improved Communication</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">📝 Recommended Actions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">Practice System Design</h5>
                  <p className="text-sm text-gray-600 mb-3">Based on your recent interviews, focus on system design concepts.</p>
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                    Start Practice →
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">Improve Problem Solving</h5>
                  <p className="text-sm text-gray-600 mb-3">Work on algorithmic thinking and coding challenges.</p>
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                    View Resources →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */}
        
        {/* Interview Details Modal */}
        {showInterviewDetails && selectedInterview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Interview Details</h2>
                  <button
                    onClick={() => {
                      setShowInterviewDetails(false);
                      setSelectedInterview(null);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Basic Info */}
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-3">Interview Information</h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="font-medium">Date:</span> {new Date(selectedInterview.SubmissionDateTime).toLocaleDateString()}</p>
                        <p><span className="font-medium">Position:</span> {selectedInterview.role || 'Not specified'}</p>
                        <p><span className="font-medium">Company:</span> {selectedInterview.company || 'Not specified'}</p>
                        <p><span className="font-medium">Level:</span> {selectedInterview.level || 'Not specified'}</p>
                        <p><span className="font-medium">Type:</span> {selectedInterview.interview_type || 'Not specified'}</p>
                        <p><span className="font-medium">Duration:</span> {selectedInterview.duration ? `${Math.floor(selectedInterview.duration / 60)}m ${selectedInterview.duration % 60}s` : 'Not available'}</p>
                        <p><span className="font-medium">Status:</span> {selectedInterview.status || 'Not specified'}</p>
                      </div>
                    </div>
                    
                    {selectedInterview.questions && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-blue-900 mb-3">Questions Asked</h3>
                        <div className="text-sm text-blue-800 whitespace-pre-wrap">
                          {selectedInterview.questions}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Right Column - Feedback */}
                  <div className="space-y-4">
                    {selectedInterview.strengths && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-green-900 mb-3">Strengths</h3>
                        <div className="text-sm text-green-800 whitespace-pre-wrap">
                          {selectedInterview.strengths}
                        </div>
                      </div>
                    )}
                    
                    {selectedInterview.improvements && (
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-yellow-900 mb-3">Areas of Improvement</h3>
                        <div className="text-sm text-yellow-800 whitespace-pre-wrap">
                          {selectedInterview.improvements}
                        </div>
                      </div>
                    )}
                    
                    {selectedInterview.session_notes && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-3">Your Notes</h3>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">
                          {selectedInterview.session_notes}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => {
                      // TODO: Implement study plan generation
                      console.log('Generate study plan for:', selectedInterview.id);
                    }}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    Generate Study Plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MockInterview; 